import React from 'react'
import registry from '../../shared/yns_icons.json'

import * as Iconoir from 'iconoir-react'
import * as HeroOutline from '@heroicons/react/24/outline'
import * as HeroSolid from '@heroicons/react/24/solid'

import LinkedInIcon from '@mui/icons-material/LinkedIn'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'

// Optional: map MUI “name” strings to real components.
const muiMap = {
  LinkedIn: LinkedInIcon,
  AccountCircle: AccountCircleIcon,
}

// heroicon "briefcase" -> "BriefcaseIcon"
function heroiconComponentName(heroName) {
  const pascal = heroName
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('')
  return `${pascal}Icon`
}

// iconoir "BrainResearch" stays "BrainResearch"
function resolveIconComponent(spec) {
  switch (spec.set) {
    case 'heroicons': {
      const variant = spec.variant || 'outline'
      const componentName = heroiconComponentName(spec.name)
      const lib = variant === 'solid' ? HeroSolid : HeroOutline
      return lib[componentName]
    }

    case 'iconoir':
      return Iconoir[spec.name]

    case 'mui':
      return muiMap[spec.name]

    default:
      return undefined
  }
}

function normalizeName(name) {
  return String(name).toLowerCase().trim().replace(/\s+/g, '-')
}

function YnsIcon({ name, className = '', size = 24, ...props }) {
  const normalizedName = normalizeName(name)
  const spec = registry[normalizedName]

  if (!spec) {
    console.warn(`Icon "${name}" not found in registry`)
    return null
  }

  const IconComponent = resolveIconComponent(spec)

  if (!IconComponent) {
    console.warn(
      `Icon "${name}" could not be resolved (set=${spec.set}, name=${spec.name}, variant=${spec.variant || 'outline'})`,
    )
    return null
  }

  // Check if className already includes width/height classes
  const hasWidthClass = /\bw-\d+\b/.test(className)
  const hasHeightClass = /\bh-\d+\b/.test(className)
  const sizeClasses =
    !hasWidthClass && !hasHeightClass ? `w-[${size}px] h-[${size}px]` : ''
  const finalClassName = `${className} ${sizeClasses}`.trim()

  return <IconComponent className={finalClassName} {...props} />
}

export default YnsIcon

export const ActionPlan = Iconoir.Strategy
export const Add = HeroOutline.PlusIcon
export const Ayla = Iconoir.MagicWand
export const Article = Iconoir.Compass
export const Assessment = Iconoir.BrainResearch

export const Attachment = HeroOutline.PaperClipIcon
export const Audio = HeroOutline.SpeakerWaveIcon
export const Back = HeroOutline.ArrowUturnRightIcon
export const Bookmark = HeroOutline.BookmarkIcon
export const Builder = HeroOutline.WrenchScrewdriverIcon
export const Calendar = HeroOutline.CalendarDaysIcon
export const Call = HeroOutline.PhoneIcon
export const Company = HeroOutline.BuildingOfficeIcon
export const Clock = Iconoir.Clock
export const Chat = Iconoir.ChatBubble
export const Checklist = HeroOutline.ClipboardDocumentListIcon
export const CoachInsights = Iconoir.DocStarIn
export const CoachNotes = HeroOutline.ChatBubbleBottomCenterTextIcon
export const CoachServices = HeroOutline.UserIcon
export const CoachServicesCircle = HeroOutline.UserCircleIcon
export const AccountCircle = AccountCircleIcon
export const Completed = HeroOutline.CheckIcon
export const Dashboard = HeroOutline.Squares2X2Icon
export const Document = HeroOutline.DocumentTextIcon
export const Download = HeroOutline.ArrowDownTrayIcon
export const Dollar = HeroOutline.BanknotesIcon
export const Email = HeroOutline.EnvelopeIcon
export const Entrepreneurship = HeroOutline.BuildingStorefrontIcon
export const Exercise = Iconoir.Bicycle
export const Eye = HeroOutline.EyeIcon
export const Gym = Iconoir.Gym
export const Heart = HeroOutline.HeartIcon
export const Interview = HeroOutline.ChatBubbleLeftRightIcon
export const InterviewDeloitte = HeroOutline.UsersIcon
export const JobTargeting = HeroOutline.BriefcaseIcon
export const Key = HeroOutline.KeyIcon
export const Library = HeroOutline.BuildingLibraryIcon
export const Linkedin = LinkedInIcon
export const List = HeroOutline.TableCellsIcon
export const Microphone = HeroOutline.MicrophoneIcon
export const Negotiation = Iconoir.CoinsSwap
export const Network = HeroOutline.UserGroupIcon
export const Networking = HeroOutline.UserGroupIcon
export const Onboarding = Iconoir.BrightStar
export const OnboardingSuccess = HeroOutline.RocketLaunchIcon
export const Orientation = HeroOutline.MapIcon
export const Pencil = HeroOutline.SparklesIcon
export const People = HeroOutline.UserGroupIcon
export const PersonalBrand = HeroOutline.SparklesIcon
export const Quiz = HeroOutline.PuzzlePieceIcon
export const Recruiting = HeroOutline.BriefcaseIcon
export const RelatedOccupations = HeroOutline.BuildingOffice2Icon
export const Research = HeroOutline.BookOpenIcon
export const Retirement = HeroOutline.TrophyIcon
export const Return = HeroOutline.ArrowUturnRightIcon
export const ReturnLeft = HeroOutline.ArrowUturnLeftIcon
export const Salary = HeroOutline.BanknotesIcon
export const SampleDoc = HeroOutline.DocumentTextIcon
export const Scripts = Iconoir.QuoteMessage
export const SearchTools = Iconoir.AreaSearch
export const Settings = HeroOutline.Cog6ToothIcon
export const ShortCourse = Iconoir.Golf
export const Skills = HeroOutline.AcademicCapIcon
export const Star = HeroOutline.StarIcon
export const StarSolid = HeroSolid.StarIcon
export const Stop = HeroOutline.StopCircleIcon
export const Tag = HeroOutline.TagIcon
export const Tasks = HeroOutline.RectangleStackIcon
export const Target = HeroOutline.BriefcaseIcon
export const TechSkills = HeroOutline.Square3Stack3DIcon
export const Tips = HeroOutline.LightBulbIcon
export const TrendingUp = HeroOutline.ArrowTrendingUpIcon
export const Tools = HeroOutline.WrenchIcon
export const Trash = HeroOutline.TrashIcon
export const Video = HeroOutline.VideoCameraIcon
export const VideoOff = HeroOutline.VideoCameraSlashIcon
export const Webinar = HeroOutline.ComputerDesktopIcon
export const Wellness = Iconoir.Donate
export const X = HeroOutline.XMarkIcon
export const EllipsisHorizontal = HeroOutline.EllipsisHorizontalIcon
export const Filter = HeroOutline.FunnelIcon
export const Review = Iconoir.PcCheck
export const Refresh = HeroOutline.ArrowPathIcon
export const Management = HeroOutline.ClipboardDocumentCheckIcon
export const Pause = HeroOutline.PauseIcon
export const Play = HeroOutline.PlayIcon
export const Mute = HeroOutline.SpeakerXMarkIcon
export const Unmute = HeroOutline.SpeakerWaveIcon
export const Warning = HeroOutline.ExclamationTriangleIcon
export const Reports = HeroOutline.ChartPieIcon
export const ResourceCenter = HeroOutline.BuildingLibraryIcon
export const RightArrow = HeroOutline.ArrowRightIcon

export const ThumbsUp = HeroOutline.HandThumbUpIcon
export const ThumbsDown = HeroOutline.HandThumbDownIcon
